import GradingMethodType from '@opsbase/lib-grading/GradingMethodType';
import GradingRuleType from '@opsbase/lib-grading/GradingRuleType';
import PassStatus from '@opsbase/lib-grading/PassStatus';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import GradeChip from '@opsbase/web-chips/GradeChip';
import PassStatusChip from '@opsbase/web-chips/PassStatusChip';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Vertical from '@opsbase/web-layout/Vertical';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import RadioGroup from '@opsbase/web-ui/RadioGroup';
import Switch from '@opsbase/web-ui/Switch';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo } from 'react';
export function ScoringSettingsSection(props) {
    const { definition, isEditable = false, onChange, simpleLayout = false } = props;
    const self = useSelf();
    const handleFieldUpdate = useCallback(field => value => {
        onChange === null || onChange === void 0 ? void 0 : onChange({ [field]: value });
    }, [onChange]);
    const gradingMethod = definition.gradingMethod;
    const isScoring = definition.isScoreEnabled;
    const isCalculated = (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingMethodType) === GradingMethodType.CALCULATED;
    const isPassFail = (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingMethodType) === GradingMethodType.PASSFAIL;
    const handleChangeGradingMethod = useCallback(typeKey => {
        const [gradingMethodType, gradingCalculator] = typeKey.split('###');
        onChange({
            gradingMethod: { ...gradingMethod, gradingMethodType, gradingCalculator },
        });
    }, [gradingMethod, onChange]);
    const handleChangeGradingMethodMinScore = useCallback(minScore => onChange({ gradingMethod: { ...gradingMethod, minScore } }), [gradingMethod, onChange]);
    const handleChangeScoringEnabled = useCallback(isScoreEnabled => {
        var _a;
        let newGradingMethod = definition.gradingMethod;
        if (!isScoreEnabled &&
            ((_a = definition.gradingMethod) === null || _a === void 0 ? void 0 : _a.gradingMethodType) === GradingMethodType.CALCULATED) {
            newGradingMethod = {
                gradingMethodType: GradingMethodType.NONE,
                minScore: 0,
                gradingCalculator: null,
            };
        }
        onChange({ isScoreEnabled, gradingMethod: newGradingMethod });
    }, [definition, onChange]);
    const InputProps = useMemo(() => ({
        style: {
            marginLeft: 4,
            marginRight: 4,
            width: 64,
            padding: '2px !important',
        },
        endAdornment: (React.createElement(Text, { variant: "small", color: "lightgrey" }, "%")),
    }), []);
    const inputProps = useMemo(() => ({
        min: 0,
        max: 100,
        style: {
            textAlign: 'right',
            padding: '2px !important',
        },
    }), []);
    const content = useMemo(() => {
        return (React.createElement(Vertical, { paddingY: true, gutter: true },
            React.createElement(Switch, { disabled: !isEditable, label: i18n.t('points-scoring-enabled'), checked: isScoring !== null && isScoring !== void 0 ? isScoring : false, onChange: handleChangeScoringEnabled }),
            React.createElement(Horizontal, { gutter: true },
                React.createElement(Card, { padding: true, style: { minWidth: 200, maxWidth: 240 } },
                    React.createElement(Text, null, "What grading mechanism"),
                    React.createElement(RadioGroup, { disabled: !isEditable, value: (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingMethodType) +
                            '###' +
                            (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingCalculator), onChange: handleChangeGradingMethod, options: [
                            {
                                value: GradingMethodType.NONE +
                                    '###' +
                                    (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingCalculator),
                                label: i18n.t('n/a'),
                            },
                            {
                                value: GradingMethodType.PASSFAIL +
                                    '###' +
                                    (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingCalculator),
                                label: i18n.t('pass-fail'),
                            },
                            ...(isScoring ? self.settings.gradingCalculators : []).map(g => ({
                                value: GradingMethodType.CALCULATED + '###' + g.key,
                                label: g.title,
                            })),
                        ] })),
                React.createElement(Card, { padding: true, grow: true },
                    // N/A
                    !isPassFail && !isCalculated && (React.createElement("ul", null,
                        React.createElement("li", null,
                            React.createElement(Text, null, "This op will not be graded")))),
                    // PASS FAIL - without scores
                    isPassFail && !isScoring && (React.createElement("ul", null,
                        descPoint(React.createElement("span", null,
                            "If an ",
                            React.createElement(Colored, null, "Immediate Fail"),
                            " answer is given, the op will"), descPassFail(PassStatus.FAIL)),
                        descPoint(React.createElement("span", null,
                            React.createElement(Colored, null, "Otherwise"),
                            ", the op will"), descPassFail(PassStatus.PASS)))),
                    // PASS FAIL - with scores
                    isPassFail && isScoring && (React.createElement("ul", null,
                        descPoint(React.createElement("span", null,
                            "If an ",
                            React.createElement(Colored, null, "Immediate Fail"),
                            " answer is given, the op will"), descPassFail(PassStatus.FAIL)),
                        React.createElement("li", { style: { margin: 8 } },
                            React.createElement(Horizontal, { centered: true, justify: "flex-start", flexWrap: "wrap" },
                                React.createElement(Text, null, i18n.t('if-score-is') +
                                    ' ' +
                                    i18n.t('greater-than')),
                                React.createElement(TextFieldWithStateAndBlur, { disabled: !isEditable, noMargin: true, value: (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.minScore) + '', onBlur: handleChangeGradingMethodMinScore, type: 'number', InputProps: InputProps, inputProps: inputProps }),
                                React.createElement(Text, null, "the op will"),
                                descPassFail(PassStatus.PASS))),
                        descPoint(React.createElement("span", null,
                            React.createElement(Colored, null, "Otherwise"),
                            ", the op will"), descPassFail(PassStatus.FAIL)))),
                    // Grade calculators
                    isCalculated &&
                        descGradingCalculator(self.settings.gradingCalculators.find(g => g.key === (gradingMethod === null || gradingMethod === void 0 ? void 0 : gradingMethod.gradingCalculator)), self.settings.grades))),
            (isScoring || isPassFail || isCalculated) && (React.createElement(Vertical, { gutter: true },
                isScoring && (React.createElement(Switch, { disabled: !isEditable, label: i18n.t('scoring-public'), checked: definition.isScorePublic, onChange: handleFieldUpdate('isScorePublic') })),
                isPassFail && (React.createElement(Switch, { disabled: !isEditable, label: i18n.t('passfail-public'), checked: definition.isGradingPublic, onChange: handleFieldUpdate('isGradingPublic') })),
                isCalculated && (React.createElement(Switch, { disabled: !isEditable, label: i18n.t('grading-public'), checked: definition.isGradingPublic, onChange: handleFieldUpdate('isGradingPublic') }))))));
    }, [
        isEditable,
        isScoring,
        handleChangeScoringEnabled,
        gradingMethod,
        handleChangeGradingMethod,
        self,
        isPassFail,
        isCalculated,
        handleChangeGradingMethodMinScore,
        InputProps,
        inputProps,
        definition,
        handleFieldUpdate,
    ]);
    if (simpleLayout)
        return content;
    return (React.createElement(Vertical, { gutter: true, padding: true },
        React.createElement(Horizontal, { gutter: true, justify: "flex-start", alignItems: "flex-bottom" },
            React.createElement(Text, { variant: "h1" }, i18n.t('scoring'))),
        React.createElement(Card, { padding: true }, content)));
}
export default ScoringSettingsSection;
function Colored({ color = Palette.secondary, children }) {
    return React.createElement("span", { style: { color: color.main } }, children);
}
function descPoint(ifBit, gBit, key) {
    return (React.createElement("li", { key: key, style: { margin: 8 } },
        React.createElement(Horizontal, { justify: "flex-left", centered: true, gutter: true },
            ifBit,
            gBit)));
}
function descGrade(g) {
    return React.createElement(GradeChip, { showPass: true, grade: g === null || g === void 0 ? void 0 : g.key });
}
function descPassFail(status) {
    return React.createElement(PassStatusChip, { status: status });
}
function descGradingCalculator(g, grades) {
    if (!g)
        return null;
    return (React.createElement("ul", null,
        descPoint(React.createElement("span", null,
            "If an ",
            React.createElement(Colored, null, "Immediate Fail"),
            " answer is given, the op will be graded as"), descGrade(grades.find(gg => gg.key === g.immediateFailGrade))),
        g.rules.map((r, idx) => {
            const grade = grades.find(gg => gg.key === r.grade);
            switch (r.type) {
                case GradingRuleType.SCORELT:
                    return descPoint(React.createElement("span", null,
                        i18n.t('if-score-is'),
                        " ",
                        React.createElement(Colored, null,
                            "less than ",
                            r.scoreLt,
                            "%"),
                        ", the op will be graded as"), descGrade(grade), 'rule' + idx);
                case GradingRuleType.SCOREGTE:
                    return descPoint(React.createElement("span", null,
                        i18n.t('if-score-is') + ' ',
                        React.createElement(Colored, null,
                            i18n.t('greater-than'),
                            " ",
                            r.scoreGte,
                            "%"),
                        ", the op will be graded as"), descGrade(grade), 'rule' + idx);
                case GradingRuleType.SCOREBETWEEN:
                    return descPoint(React.createElement("span", null,
                        i18n.t('if-score-is'),
                        ' ',
                        React.createElement(Colored, null,
                            "between ",
                            r.scoreGte,
                            "% and ",
                            r.scoreLt,
                            "%"),
                        ", the op will be graded as"), descGrade(grade), 'rule' + idx);
            }
        }),
        descPoint(React.createElement("span", null,
            React.createElement(Colored, null, "Otherwise"),
            ", the op will be graded as"), descGrade(grades.find(gg => gg.key === g.failoverGrade)))));
}
